import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MobileScrollRail } from './MobileScrollRail';
import './Gallery.css';
import {
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaImage,
} from 'react-icons/fa';
import { fetchGallery } from '../firebase/firestoreService';

import alotImage from '../assets/alot.jpg';
import mommyImage from '../assets/mommy.jpeg';
import runwayImage from '../assets/runway.jpg';
import thImage from '../assets/TH.jpg';
import fnafImage from '../assets/fnaf.jpg';
import frozenImage from '../assets/frozen.jpg';
import galleryRoomImage from '../assets/GALLERYROOM.jpg';

function formatCategoryLabel(raw) {
  if (raw == null || raw === '') return '';
  const s = String(raw).trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export default function Gallery() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('All');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchGallery();
        const transformed = data.map((g) => ({
          id: g.id,
          src: g.imageUrl,
          title: g.title,
          category: g.category || 'Featured',
          createdAt: g.createdAt,
          type: 'image',
        }));
        setItems(transformed);
      } catch (err) {
        console.error('Error loading gallery:', err);
        setError('Failed to load gallery. Please try again later.');
        setItems([
          { id: '1', src: alotImage, title: 'A lot Like Life', category: 'Featured', type: 'image' },
          { id: '2', src: mommyImage, title: 'mummy 2023', category: 'Featured', type: 'image' },
          { id: '3', src: runwayImage, title: 'Runway (Sky Siege)', category: 'Featured', type: 'image' },
          { id: '4', src: thImage, title: 'The Howling', category: 'Featured', type: 'image' },
          { id: '5', src: fnafImage, title: "Five Nights at Freddy's", category: 'Featured', type: 'image' },
          { id: '6', src: frozenImage, title: 'Frozen Experience', category: 'Featured', type: 'image' },
          { id: '7', src: galleryRoomImage, title: 'Gallery Room', category: 'Featured', type: 'image' },
        ]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const categories = useMemo(() => {
    const base = ['All'];
    const uniq = Array.from(new Set(items.map((i) => i.category).filter(Boolean)));
    return base.concat(uniq);
  }, [items]);

  const filteredMedia = useMemo(
    () =>
      activeCategory === 'All'
        ? items
        : items.filter((item) => item.category === activeCategory),
    [items, activeCategory],
  );

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const scroll = (direction) => {
    const amount = 320;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: 'smooth',
      });
    }
  };

  const nextMedia = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredMedia.length);
  };

  const prevMedia = () => {
    setCurrentIndex((prev) => (prev - 1 + filteredMedia.length) % filteredMedia.length);
  };

  const current = filteredMedia.length > 0 ? filteredMedia[currentIndex] : null;

  return (
    <div className="gallery-component">
      <header className="gallery-header">
        <span className="gallery-section-tag">Portfolio</span>
        <h2 className="gallery-title">Project Gallery</h2>
        <div className="gallery-header-divider" aria-hidden />
        <p className="gallery-subtitle">
          Visual showcase of our creative work and behind-the-scenes moments
        </p>
      </header>

      <div className="gallery-filters" role="tablist" aria-label="Filter gallery by category">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            role="tab"
            aria-selected={activeCategory === category}
            className={`gallery-filter-btn ${activeCategory === category ? 'is-active' : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {formatCategoryLabel(category)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="gallery-loading">
          <div className="gallery-loading-spinner" />
          <p>Loading gallery…</p>
        </div>
      ) : error ? (
        <div className="gallery-error">
          <p className="gallery-error-message">{error}</p>
          <button type="button" className="gallery-retry-btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      ) : filteredMedia.length > 0 ? (
        <div className="gallery-container">
          <button
            type="button"
            className="gallery-scroll-arrow gallery-scroll-arrow--left"
            onClick={() => scroll('left')}
            aria-label="Scroll gallery left"
          >
            <FaChevronLeft />
          </button>

          <div className="gallery-cards" ref={scrollContainerRef}>
            {filteredMedia.map((item, index) => (
              <article
                key={item.id}
                className="gallery-card"
                role="button"
                tabIndex={0}
                aria-label={item.title ? `Open ${item.title} in viewer` : `Open gallery image ${index + 1}`}
                onClick={() => openLightbox(index)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openLightbox(index);
                  }
                }}
              >
                <div className="gallery-card-media">
                  {item.src ? (
                    <img src={item.src} alt="" className="gallery-card-image" />
                  ) : (
                    <div className="gallery-card-placeholder">
                      <FaImage className="gallery-placeholder-icon" aria-hidden />
                      <span className="gallery-placeholder-label">Coming soon</span>
                    </div>
                  )}
                  <div className="gallery-card-scrim" aria-hidden />
                  <div className="gallery-card-overlay">
                    <span className="gallery-view-pill">
                      <FaExpand className="gallery-view-pill-icon" aria-hidden />
                      View
                    </span>
                  </div>
                </div>
                <div className="gallery-card-footer">
                  {item.category && (
                    <span className="gallery-card-category">{formatCategoryLabel(item.category)}</span>
                  )}
                  {item.title && <h3 className="gallery-card-title">{item.title}</h3>}
                </div>
              </article>
            ))}
          </div>

          <MobileScrollRail scrollerRef={scrollContainerRef} />

          <button
            type="button"
            className="gallery-scroll-arrow gallery-scroll-arrow--right"
            onClick={() => scroll('right')}
            aria-label="Scroll gallery right"
          >
            <FaChevronRight />
          </button>
        </div>
      ) : (
        <div className="gallery-empty">
          <p>No gallery items available at the moment.</p>
        </div>
      )}

      {lightboxOpen && current && (
        <div
          className="gallery-lightbox"
          onClick={closeLightbox}
          role="presentation"
        >
          <div
            className="gallery-lightbox-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="gallery-lightbox-title"
          >
            <button
              type="button"
              className="gallery-lightbox-close"
              onClick={closeLightbox}
              aria-label="Close viewer"
            >
              &times;
            </button>

            <div className="gallery-lightbox-stage">
              {filteredMedia.length > 1 && (
                <button
                  type="button"
                  className="gallery-lightbox-nav gallery-lightbox-nav--prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevMedia();
                  }}
                  aria-label="Previous image"
                >
                  <FaChevronLeft />
                </button>
              )}

              {current.type === 'image' && current.src ? (
                <img
                  src={current.src}
                  alt={current.title || `Image ${currentIndex + 1}`}
                  className="gallery-lightbox-image"
                />
              ) : (
                <p className="gallery-lightbox-unsupported">Unsupported media in viewer</p>
              )}

              {filteredMedia.length > 1 && (
                <button
                  type="button"
                  className="gallery-lightbox-nav gallery-lightbox-nav--next"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextMedia();
                  }}
                  aria-label="Next image"
                >
                  <FaChevronRight />
                </button>
              )}
            </div>

            <div className="gallery-lightbox-meta">
              {current.title && (
                <h3 id="gallery-lightbox-title" className="gallery-lightbox-heading">
                  {current.title}
                </h3>
              )}
              {filteredMedia.length > 1 && (
                <span className="gallery-lightbox-counter" aria-live="polite">
                  {currentIndex + 1} / {filteredMedia.length}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
